import { useTranslation } from '@/composables';
import {
	SuiDataTableColumnAlign,
	type SuiDataTableColumnType,
} from '@developers/scoro-ui';
import {
	TABLE_COLUMN,
	COLUMN_TRANSLATION_KEYS,
	COLUMN_HEADER_SORT_MAP,
} from './constants';
import { computed, type Ref } from 'vue';
import { type SortState } from './types';
import { useCurrentUser } from '@/features/Auth';
import {
	useTaskListColumnSettings,
	type TaskListColumnSettingsOptions,
} from './TaskListColumnToggle';

export const useTaskList = ({
	sortState,
	showSubtasks,
	useTaskStatuses,
	storageKey,
	availableColumns,
}: {
	sortState: SortState;
	showSubtasks: Ref<boolean>;
	useTaskStatuses: boolean;
	storageKey?: string;
	availableColumns?: readonly string[];
}) => {
	const { t } = useTranslation();
	const { isCUPOUser } = useCurrentUser();

	const columnSettingsOptions: TaskListColumnSettingsOptions = {};
	if (storageKey) {
		columnSettingsOptions.storageKey = storageKey;
	}

	if (availableColumns) {
		columnSettingsOptions.availableColumns = availableColumns;
	}

	const { getOrderedColumnIds, isColumnVisible } = useTaskListColumnSettings(
		columnSettingsOptions
	);

	const getColumnTitle = (columnId: string): string => {
		const key = COLUMN_TRANSLATION_KEYS[columnId];
		return key ? t(key) : '';
	};

	const baseColDefinition: SuiDataTableColumnType[] = [
		{
			id: TABLE_COLUMN.Collapsible,
			title: '',
			width: '2rem',
			iconName: 'icon-expanddown-filled-16',
			iconLabel: t('expand_all'),
			isInteractive: true,
			class: 'task-list__expand-collapse',
		},
		{
			id: TABLE_COLUMN.Assignee,
			title: getColumnTitle(TABLE_COLUMN.Assignee),
			width: '4%',
			class: 'task-list__assignee',
		},
		{
			id: TABLE_COLUMN.Actions,
			title: '',
			width: '10rem',
		},
		{
			id: TABLE_COLUMN.Title,
			title: getColumnTitle(TABLE_COLUMN.Title),
			titleLabel: t('tasks_sort_by_title'),
		},
		{
			id: TABLE_COLUMN.Subitems,
			title: getColumnTitle(TABLE_COLUMN.Subitems),
			width: '5%',
			align: SuiDataTableColumnAlign.CENTER,
			titleLabel: t('tasks_sort_by_subitems'),
		},
		{
			id: TABLE_COLUMN.Comments,
			title: getColumnTitle(TABLE_COLUMN.Comments),
			titleLabel: t('tasks_sort_by_comments'),
			width: '5%',
			align: SuiDataTableColumnAlign.RIGHT,
		},
		{
			id: TABLE_COLUMN.Status,
			title: getColumnTitle(TABLE_COLUMN.Status),
			titleLabel: t('tasks_sort_by_status'),
			width: '6%',
		},
		{
			id: TABLE_COLUMN.ActivityType,
			title: getColumnTitle(TABLE_COLUMN.ActivityType),
			width: '12%',
		},
		{
			id: TABLE_COLUMN.Progress,
			title: getColumnTitle(TABLE_COLUMN.Progress),
			width: '8%',
			align: SuiDataTableColumnAlign.CENTER,
		},
		{
			id: TABLE_COLUMN.StartDate,
			title: getColumnTitle(TABLE_COLUMN.StartDate),
			titleLabel: t('tasks_sort_by_start_date'),
			width: '5%',
			align: SuiDataTableColumnAlign.RIGHT,
		},
		{
			id: TABLE_COLUMN.DueDate,
			title: getColumnTitle(TABLE_COLUMN.DueDate),
			titleLabel: t('tasks_sort_by_due_date'),
			width: '5%',
			align: SuiDataTableColumnAlign.RIGHT,
		},
		{
			id: TABLE_COLUMN.Budget,
			title: getColumnTitle(TABLE_COLUMN.Budget),
			width: '5%',
			align: SuiDataTableColumnAlign.RIGHT,
		},
		{
			id: TABLE_COLUMN.RemainingBudget,
			title: getColumnTitle(TABLE_COLUMN.RemainingBudget),
			width: '6%',
			align: SuiDataTableColumnAlign.RIGHT,
		},
		{
			id: TABLE_COLUMN.Planned,
			title: getColumnTitle(TABLE_COLUMN.Planned),
			titleLabel: t('tasks_sort_by_planned'),
			width: '5%',
			align: SuiDataTableColumnAlign.RIGHT,
		},
		{
			id: TABLE_COLUMN.Done,
			title: getColumnTitle(TABLE_COLUMN.Done),
			titleLabel: t('tasks_sort_by_time_spent'),
			width: '5%',
			align: SuiDataTableColumnAlign.RIGHT,
		},
		{
			id: TABLE_COLUMN.Remaining,
			title: getColumnTitle(TABLE_COLUMN.Remaining),
			titleLabel: t('tasks_sort_by_remaining'),
			width: '5%',
			align: SuiDataTableColumnAlign.RIGHT,
		},
		{
			id: TABLE_COLUMN.MarkDone,
			title: '',
			width: '7rem',
			align: SuiDataTableColumnAlign.RIGHT,
		},
	];

	const colDefinition = computed(() => {
		const conditionallyFiltered = baseColDefinition.filter((col) => {
			if (
				!showSubtasks.value &&
				[TABLE_COLUMN.Collapsible, TABLE_COLUMN.Subitems].includes(
					col.id
				)
			) {
				return false;
			}

			if (isCUPOUser.value && col.id === TABLE_COLUMN.ActivityType) {
				return false;
			}

			if (!useTaskStatuses && col.id === TABLE_COLUMN.Status) {
				return false;
			}

			return true;
		});

		const visibilityFiltered = conditionallyFiltered.filter((col) =>
			isColumnVisible(col.id)
		);

		const orderedColumnIds = getOrderedColumnIds();
		const orderedColumns = orderedColumnIds
			.map((id) => visibilityFiltered.find((col) => col.id === id))
			.filter((col): col is SuiDataTableColumnType => col !== undefined);

		return orderedColumns.map((col) => {
			const sortable = COLUMN_HEADER_SORT_MAP[col.id];
			return {
				...col,
				isInteractive: !!sortable || col.isInteractive,
				sort: sortable
					? {
							isActive: sortState.columnId === col.id,
							direction: sortState.direction,
						}
					: undefined,
			};
		});
	});

	return {
		sortState,
		colDefinition,
	};
};
